import { NO_TEXT, type Translated, verbatim } from '@myre/common-i18n';
import { type ISelectItem } from '@myre/ui-primitive-forms';
import { Theme } from '@myre/ui-primitive-list-paginated';

import { type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ActivatedRoute } from '@angular/router';
import { castArray, isNil } from 'lodash-es';
import { filter, map, startWith } from 'rxjs/operators';

import {
  type FilterOption,
  type FilterValue,
} from '../filter-controls-panel/interfaces/filters/base.filter';
import { isEmptyFilterValue } from '../helpers/filters.helpers';
import { LIST_THEME } from '../store/interfaces/index';

export function formatOptions<
  FilterKey extends string,
  T extends {
    id: number;
    label: string;
    dependencies?: { key: FilterKey; value: number }[];
  },
>(
  items: T[],
  options?: {
    value?: (item: T) => FilterValue;
    /** Names the option; without it, the item's own label is shown as is. */
    label?: (item: T) => Translated;
  },
): FilterOption<FilterKey>[] | null {
  // Backend has not returned yet.
  if (items === undefined) {
    return;
  }

  return (items || []).map(item => ({
    value: options?.value ? options.value(item) : item.id,
    label: options?.label ? options.label(item) : verbatim(item.label),
    dependencies: item.dependencies,
  }));
}

/** ISelectItem allows a bare value; the filter panel wants every option labelled. */
export function iSelectItemsToFilterOptions(
  items: readonly ISelectItem<number, string>[],
): FilterOption[] {
  return items.map(item => ({
    value: item.value,
    label: item.label ?? NO_TEXT,
  }));
}

/**
 * This function is used to cast filter value to an API friendly format.
 * Only use this cast on a filtering context, while being confident regarding typing.
 *
 * Only the empty entries are dropped: `0` is a selectable option value (a ground
 * floor, a rate of zero), so it has to reach the API like any other.
 */
export function castToArray<T>(value: FilterValue): T[] | null {
  if (isNil(value)) {
    return null;
  }

  return castArray(value).filter(item => !isEmptyFilterValue(item)) as T[];
}

export function getListTheme(activatedRoute: ActivatedRoute): Signal<Theme> {
  return toSignal(
    activatedRoute.data.pipe(
      map(data => data?.[LIST_THEME] as Theme),
      filter(theme => !!theme),
      startWith(Theme.StandaloneCard),
    ),
  );
}
